import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { Operator } from "../models/Operator";
import { OPERATOR_INITIAL_VALUES } from "../models/Operator";
import { useOperators } from "../hooks/useOperators";
import {
	useDeleteOperator,
	useUpsertOperator,
} from "../hooks/useOperatorMutation";
import { OperatorsTable } from "./OperatorsTable";
import { OperatorDetailViewer } from "./OperatorDetailViewer";
import { OperatorsToolbar } from "./OperatorsToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	operatorId: urlParam.int(),
};

export const OperatorsManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{ search, onlyActives, operatorId },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [operatorToDelete, setOperatorToDelete] = useState<Operator | null>(
		null,
	);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "operators-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const { operators, isLoading, error: queryError } = useOperators();

	const filteredOperators = useMemo(() => {
		const base = onlyActives ? operators.filter((o) => o.Active) : operators;
		if (!searchText) return base;
		const s = searchText.toLowerCase();
		return base.filter(
			(o) =>
				o.Name?.toLowerCase().includes(s) ||
				o.ClockNumber?.toLowerCase().includes(s),
		);
	}, [operators, searchText, onlyActives]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<Operator>({
		id: operatorId,
		setId: (value) => setFilters({ operatorId: value }),
		dropId: () => dropValues("operatorId"),
		rows: operators,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedOperator = selection.current;

	const { upsertOperator, isUpserting } = useUpsertOperator({
		onSuccess: (data) => {
			const isNew = !selectedOperator?.Id;
			setNotification(
				isNew
					? "Operator created successfully"
					: "Operator updated successfully",
			);
			if (data) {
				selection.adopt(data);
			}
		},
		onError: (err) => setError(err.message),
	});

	const { deleteOperator, isDeleting } = useDeleteOperator({
		onSuccess: () => {
			setNotification("Operator deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setOperatorToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const handleRowSelected = useCallback(
		(record: Operator | null) => {
			if (record) selection.select(record);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((operator: Operator) => {
		setOperatorToDelete(operator);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (operatorToDelete?.Id) {
			deleteOperator(operatorToDelete.Id);
		}
	}, [operatorToDelete, deleteOperator]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setOperatorToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...OPERATOR_INITIAL_VALUES });
	}, []);

	const handleSave = useCallback(
		(operator: Operator) => {
			upsertOperator(operator);
		},
		[upsertOperator],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, []);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Operators
			</Typography>

			<Paper elevation={3}>
				<OperatorsToolbar
					onReset={() => resetValues(["search", "onlyActives"])}
					canReset={!isDefault("search", "onlyActives")}
					search={searchText}
					onSearchChange={setSearchText}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAdd}
				/>
			</Paper>

			<Group
				id="operators-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<OperatorsTable
									operators={filteredOperators}
									selectedOperator={
										selectedOperator?.Id ? selectedOperator : null
									}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							<OperatorDetailViewer
								operator={selectedOperator}
								existingOperators={operators}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE OPERATOR"
				itemName={operatorToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
